import type { ReactNode } from "react";
import { Box, Skeleton, Table, TableBody, TableCell, TableHead, TableRow, useMediaQuery } from "@mui/material";
import type { Theme } from "@mui/material";
import AddRoadIcon from "@mui/icons-material/AddRoad";
import PaymentIcon from "@mui/icons-material/Payment";
import { TheorieunterrichtIcon } from "../icons/TheorieunterrichtIcon";
import { CarIcon } from "../icons/CarIcon";
import { ManualGearIcon } from "../icons/ManualGearIcon";
import { SimulatorIcon } from "../icons/SimulatorIcon";

export function StudentsListSkeleton(props: {
  variant?: "content" | "full";
  rowCount?: number;
  showBranch?: boolean;
  showSimulatorDrivingLessons?: boolean;
  showStatusFilter?: boolean;
}) {
  const {
    variant = "content",
    rowCount = 10,
    showBranch = true,
    showSimulatorDrivingLessons = true,
    showStatusFilter = true,
  } = props;
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const columns: { id: string; label: ReactNode; width: number | string; numeric?: boolean }[] = [
    { id: "name", label: "Name", width: "22%" },
    ...(showBranch ? [{ id: "branch", label: "Filiale", width: "13%" }] : []),
    { id: "training", label: "Ausbildung", width: "13%" },
    { id: "startDate", label: "Anmeldung", width: "10%" },
    { id: "theory", label: <TheorieunterrichtIcon />, width: 56, numeric: true },
    ...(showSimulatorDrivingLessons ? [{ id: "simulator", label: <SimulatorIcon />, width: 56, numeric: true }] : []),
    { id: "driving", label: <CarIcon />, width: 56, numeric: true },
    { id: "special", label: <AddRoadIcon />, width: 56, numeric: true },
    { id: "manual", label: <ManualGearIcon />, width: 56, numeric: true },
    { id: "status", label: "Status", width: "12%" },
    { id: "balance", label: "Saldo", width: "9%" },
    { id: "payment", label: <PaymentIcon />, width: 56 },
  ];

  return (
    <Box
      role="status"
      aria-label={"Fahrsch\u00fcler werden geladen"}
      aria-busy="true"
      data-testid="StudentsListSkeleton"
      sx={{
        "@media (prefers-reduced-motion: reduce)": {
          "& .MuiSkeleton-root, & .MuiSkeleton-root::after": { animation: "none" },
        },
      }}
    >
      {variant === "full" && (
        <Box
          aria-hidden="true"
          data-testid="StudentsListToolbarSkeleton"
          sx={{ display: "flex", alignItems: "center", gap: 3, overflow: "hidden", mb: "10px", height: 64 }}
        >
          <Skeleton
            variant="rounded"
            width={isMobile ? 180 : 200}
            height={36}
            sx={{ flexShrink: 0, borderRadius: 5 }}
          />
          {[...(showBranch ? [75] : []), ...(showStatusFilter ? [75] : []), 150, 75].map((width, index) => (
            <Skeleton key={index} variant="text" width={width} sx={{ flexShrink: 0 }} />
          ))}
          <Skeleton variant="text" width={130} sx={{ flexShrink: 0 }} />
          <Box sx={{ flex: 1 }} />
          <Skeleton variant="rounded" width={76} height={30} sx={{ flexShrink: 0 }} />
        </Box>
      )}
      {isMobile ? (
        <Box aria-hidden="true" sx={{ display: "flex", flexDirection: "column", gap: 1, py: "1px" }}>
          {Array.from({ length: rowCount }, (_value, index) => (
            <Box key={index} sx={{ display: "flex", gap: 1.5, borderBottom: 1, borderColor: "divider", py: 1 }}>
              <Skeleton variant="circular" width={40} height={40} sx={{ flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Skeleton variant="rounded" width={90} height={18} />
                <Skeleton variant="text" width="70%" sx={{ fontSize: 16, fontWeight: 600 }} />
                <Skeleton variant="text" width="55%" sx={{ fontSize: 12 }} />
                <Skeleton variant="text" width="95%" sx={{ fontSize: 12, my: 0.5 }} />
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                  <Skeleton variant="text" width="35%" sx={{ fontSize: 12 }} />
                  <Skeleton variant="text" width={65} sx={{ fontSize: 12 }} />
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Box className="RaDatagrid-root students-list" aria-hidden="true">
          <Box className="RaDatagrid-tableWrapper">
            <Table
              size="small"
              className="RaDatagrid-table"
              sx={{ tableLayout: "fixed", minWidth: showBranch ? 1000 : 850 }}
            >
              <TableHead>
                <TableRow sx={{ height: 60 }}>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      className="RaDatagrid-headerCell"
                      align={column.numeric ? "right" : "left"}
                      sx={{ width: column.width, color: "text.secondary", fontWeight: "normal" }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.from({ length: rowCount }, (_value, index) => (
                  <TableRow key={index} sx={{ height: 60 }}>
                    {columns.map((column) => (
                      <TableCell key={column.id}>
                        <_CellSkeleton column={column.id} numeric={column.numeric} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Box>
      )}
      {variant === "full" && (
        <Box
          aria-hidden="true"
          sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 2, height: 48 }}
        >
          <Skeleton variant="text" width={100} />
          <Skeleton variant="text" width={80} />
        </Box>
      )}
    </Box>
  );
}

function _CellSkeleton(props: { column: string; numeric?: boolean }) {
  const { column, numeric } = props;
  if (column === "name") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <Skeleton variant="circular" width={32} height={32} sx={{ flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Skeleton variant="rounded" width={90} height={18} />
          <Skeleton variant="text" width="85%" sx={{ fontSize: 14 }} />
        </Box>
      </Box>
    );
  }
  if (column === "training" || column === "payment") {
    return <Skeleton variant="rounded" width={column === "training" ? 60 : 28} height={20} sx={{ maxWidth: "100%" }} />;
  }
  if (column === "status") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Skeleton variant="circular" width={10} height={10} sx={{ flexShrink: 0 }} />
        <Skeleton variant="text" width="65%" sx={{ fontSize: 14 }} />
      </Box>
    );
  }
  return <Skeleton variant="text" width={numeric ? 24 : "75%"} sx={{ fontSize: 14, ml: numeric ? "auto" : 0 }} />;
}
